import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { Card } from "../common/Card";
import { ErrorRetry } from "../../common/ErrorRetry";
import { PieChartShimmer } from "../../common/reportDashBShimmer/PieChartShimmer";
import {
  HOTEL_CHART_COLORS,
  formatHotelMoney,
  formatHotelPercentage,
} from "./hotelReportUtils";

export function HotelBreakdownsSection({
  state,
  revenueByUserType,
  revenueBySupplier,
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {state.loading ? (
        <PieChartShimmer />
      ) : revenueByUserType.length > 0 ? (
        <Card className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
          <span className="mb-6 text-xl text-[#1A1A1A]">Revenue by User Type</span>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={revenueByUserType}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={3}
                dataKey="value"
                label={({ name, sharePercentage }) =>
                  `${name} ${formatHotelPercentage(sharePercentage)}`
                }
              >
                {revenueByUserType.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={HOTEL_CHART_COLORS[index % HOTEL_CHART_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                }}
                formatter={(value) => formatHotelMoney(value)}
              />
            </PieChart>
          </ResponsiveContainer>

          <div className="mt-4 space-y-3">
            {revenueByUserType.map((entry, index) => (
              <div
                key={`${entry.userType}-${index}`}
                className="rounded-lg bg-[#F8F9FC] p-4"
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-[#1A1A1A]">{entry.userType}</div>
                    <div className="text-sm text-[#6B7280]">
                      {entry.bookings} bookings
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[#1A1A1A]">
                      {formatHotelMoney(entry.revenue)}
                    </div>
                    <div className="text-sm text-[#1E61DC]">
                      {formatHotelPercentage(entry.sharePercentage)} share
                    </div>
                  </div>
                </div>
                {entry.retailInfo?.info && (
                  <div className="mt-2 text-xs text-[#6B7280]">
                    {entry.retailInfo.info}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Card className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
          <div
            className={`h-[300px] flex items-center justify-center ${
              state.error ? "text-red-500" : "text-gray-500"
            }`}
          >
            {state.error ? (
              <ErrorRetry message={state.message} onRetry={state.onRetry} />
            ) : (
              "No user type breakdown available"
            )}
          </div>
        </Card>
      )}

      <Card className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
        <span className="mb-6 text-xl text-[#1A1A1A]">Revenue by Supplier</span>
        {state.loading ? (
          <div className="space-y-4 animate-pulse">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="h-24 rounded-lg bg-gray-100" />
            ))}
          </div>
        ) : state.error ? (
          <div className="h-[300px] flex items-center justify-center">
            <ErrorRetry message={state.message} onRetry={state.onRetry} />
          </div>
        ) : revenueBySupplier.length > 0 ? (
          <div className="space-y-4">
            {revenueBySupplier.map((supplier, index) => (
              <div
                key={`${supplier.supplier}-${index}`}
                className="rounded-lg bg-[#F8F9FC] p-4"
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-[#1A1A1A]">{supplier.supplier}</div>
                    <div className="text-sm text-[#6B7280]">
                      {supplier.bookings} bookings
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[#1A1A1A]">
                      {formatHotelMoney(supplier.revenue)}
                    </div>
                    <div className="text-sm text-[#1E61DC]">
                      {formatHotelPercentage(supplier.sharePercentage)} share
                    </div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg bg-white p-3">
                    <div className="text-[#6B7280]">Supplier Cost</div>
                    <div className="text-[#1A1A1A]">
                      {formatHotelMoney(supplier.supplierCost)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white p-3">
                    <div className="text-[#6B7280]">Gross Margin</div>
                    <div className="text-[#1A1A1A]">
                      {formatHotelMoney(supplier.grossMargin)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-gray-500">
            No supplier breakdown available
          </div>
        )}
      </Card>
    </div>
  );
}
